/**
 * DemoCall — lecteur animé d'un appel modèle (Mode Démonstration).
 * Réplique de l'interface du mode Simulation : chat à droite, protocole qui se
 * remplit automatiquement à gauche, notes horodatées, priorité, impression finale.
 */
import { useState, useEffect, useRef, useMemo } from 'react';
import { Play, Pause, RotateCcw, ArrowLeft, StepForward, Lightbulb, FileText, Flag, CheckCircle, Printer, Radio, Bot, User, Activity, ShieldAlert, GraduationCap, TrendingUp, Sparkles, Volume2, VolumeX } from 'lucide-react';
import { DEMO_CALLS } from './demoCallScripts';
import { escHtml, buildPhasesHtml, buildNotesHtml, CallNotesCard } from './callReport';
import { formatChrono } from './PreArrivalPhase';

const PRIORITY_CONFIG = {
  1: { label: 'PRIORITÉ 1', title: 'Urgence vitale', color: '#DC2626', bg: '#FEE2E2' },
  2: { label: 'PRIORITÉ 2', title: 'Urgent — critique', color: '#F59E0B', bg: '#FEF3C7' },
  4: { label: 'PRIORITÉ 4', title: 'Évaluation en cours / stable', color: '#22C55E', bg: '#DCFCE7' },
};

const stepDelay = (step) => {
  const len = (step.text || '').length;
  if (step.t === 'transfer') return Math.min(6500, Math.max(3200, len * 30));
  if (step.t === 'c' || step.t === 'd') return Math.min(4200, Math.max(1600, len * 32));
  if (step.t === 'tip') return 1200;
  if (step.t === 'priority') return 2800;
  if (step.t === 'phase') return 1100;
  if (step.t === 'ans') return 900;
  if (step.t === 'note') return 1300;
  return 2500;
};

// ---------- Read-only phase renderer (mirrors HealthSimulation mini-protocol) ----------
const DemoPhaseView = ({ phase, answers }) => {
  if (!phase) return null;

  if (phase.type === 'info_gathering') {
    return (
      <div className="space-y-1.5" data-testid="demo-phase-info">
        {phase.fields.map(f => (
          <div key={f.id} className="flex items-center justify-between gap-2 p-1.5 rounded border bg-white text-xs" style={{ borderColor: answers[f.id] ? '#BFDBFE' : '#F3F4F6' }}>
            <span className="text-gray-500 flex-shrink-0">{f.label}</span>
            <span key={String(answers[f.id] || '')} className={`font-semibold text-right ${answers[f.id] ? 'text-blue-800 demo-fill-pulse rounded px-1' : 'text-gray-300'}`}>{answers[f.id] || '—'}</span>
          </div>
        ))}
      </div>
    );
  }

  if (phase.type === 'open_question') {
    const noted = Array.isArray(answers[phase.id]) ? answers[phase.id] : [];
    return (
      <div data-testid="demo-phase-open">
        <div className="p-2 rounded bg-blue-50 border border-blue-200 mb-2">
          <p className="text-xs font-medium text-blue-800 italic">"{phase.question}"</p>
        </div>
        <div className="flex flex-wrap gap-1">
          {phase.elements_recherches.map(el => (
            <span key={`${el}-${noted.includes(el)}`} className={`text-[11px] px-1.5 py-0.5 rounded transition-colors ${noted.includes(el) ? 'bg-green-100 font-semibold text-green-800 demo-fill-pulse' : 'bg-gray-50 text-gray-400'}`}>
              {noted.includes(el) ? '✓ ' : ''}{el}
            </span>
          ))}
        </div>
      </div>
    );
  }

  if (phase.type === 'decision' || phase.type === 'comparison') {
    const chosen = answers[phase.id];
    return (
      <div data-testid="demo-phase-decision">
        {phase.question && (
          <div className="p-2 rounded bg-blue-50 border border-blue-200 mb-2">
            <p className="text-xs font-medium text-blue-800 italic">"{phase.question}"</p>
          </div>
        )}
        {phase.type === 'comparison' && phase.anxiety_symptoms && (
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div className="p-2 rounded bg-amber-50 border border-amber-200">
              <h4 className="text-[10px] font-bold text-amber-800 mb-1">Anxiété</h4>
              {phase.anxiety_symptoms.map(s => <p key={s} className="text-[10px] text-amber-700">• {s}</p>)}
            </div>
            <div className="p-2 rounded bg-red-50 border border-red-200">
              <h4 className="text-[10px] font-bold text-red-800 mb-1">Drapeaux rouges</h4>
              {(phase.red_flags_cardiac || []).map(s => <p key={s} className="text-[10px] text-red-700">• {s}</p>)}
            </div>
          </div>
        )}
        <div className="space-y-1">
          {phase.answers.map(a => {
            const sel = chosen === a.label;
            return (
              <div
                key={`${a.id}-${sel}`}
                className={`w-full text-left p-2 rounded border text-xs transition-all ${sel ? 'demo-fill-pulse' : ''}`}
                style={{
                  borderColor: sel ? '#2E6CF5' : a.alert ? '#FCA5A5' : '#E5E7EB',
                  backgroundColor: sel ? '#EFF6FF' : a.alert ? '#FEF2F2' : 'white',
                }}
              >
                <span className={`font-semibold ${sel ? 'text-blue-800' : 'text-gray-600'}`}>{a.label}</span>
                {sel && <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">✓</span>}
                {a.alert && <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">ALERTE</span>}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (phase.type === 'multi_question') {
    const vals = answers[phase.id] || {};
    return (
      <div className="space-y-1.5" data-testid="demo-phase-multi">
        {phase.questions.map(q => {
          const v = vals[q.id];
          const filled = v != null && String(v).trim() !== '';
          const rf = (q.red_flag && v === 'oui') || (q.type === 'multi_choice' && (q.red_flag_options || []).includes(v));
          const label = q.yes_label && v === 'oui' ? q.yes_label : q.no_label && v === 'non' ? q.no_label : String(v);
          return (
            <div key={q.id} className="p-2 rounded border text-xs bg-white transition-colors" style={{ borderColor: rf ? '#FCA5A5' : filled ? '#BFDBFE' : '#F3F4F6' }}>
              <p className={`mb-1 ${filled ? 'font-medium text-gray-700' : 'text-gray-400'}`}>
                {rf && <ShieldAlert className="inline w-3 h-3 mr-0.5 text-red-500" />}
                {q.question}
              </p>
              {filled ? (
                <span className={`demo-fill-pulse inline-block px-2 py-0.5 rounded font-semibold ${rf ? 'bg-red-100 text-red-700' : String(v) === 'oui' ? 'bg-green-100 text-green-700' : String(v) === 'inconnue' ? 'bg-amber-100 text-amber-700' : 'bg-blue-50 text-blue-700'}`}>
                  {label}
                </span>
              ) : (
                <span className="text-gray-300">—</span>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return null;
};

const scoreColor = (s) => (s >= 70 ? '#16A34A' : s >= 50 ? '#F59E0B' : '#DC2626');

// Exemple d'évaluation automatisée (grille réelle des instructeurs)
const DemoEvaluationCard = ({ evaluation }) => (
  <div className="rounded-2xl border-2 border-indigo-200 bg-white p-4 sm:p-5 space-y-4" data-testid="demo-evaluation-card">
    <div className="flex items-start gap-2">
      <GraduationCap className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#002768' }} />
      <div>
        <h3 className="text-sm font-bold" style={{ color: '#002768' }}>Exemple d'évaluation automatisée de l'appel</h3>
        <p className="text-[11px] text-gray-400">
          Grille établie par les instructeurs (pondération ajustable). Chaque appel étudiant reçoit automatiquement ce rapport détaillé.
        </p>
      </div>
    </div>

    <div className="flex flex-col sm:flex-row items-center gap-4">
      <div className="flex-shrink-0" data-testid="demo-eval-gauge">
        <svg width="110" height="110" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r="52" fill="none" stroke="#E5E7EB" strokeWidth="12" />
          <circle
            cx="60" cy="60" r="52" fill="none"
            stroke={scoreColor(evaluation.score_global)} strokeWidth="12" strokeLinecap="round"
            strokeDasharray={`${(evaluation.score_global / 100) * 326.7} 326.7`}
            transform="rotate(-90 60 60)"
          />
          <text x="60" y="66" textAnchor="middle" fontSize="26" fontWeight="bold" fill={scoreColor(evaluation.score_global)}>{evaluation.score_global}</text>
          <text x="60" y="82" textAnchor="middle" fontSize="10" fill="#9CA3AF">/ 100</text>
        </svg>
      </div>
      <div className="flex-1 w-full space-y-2">
        {evaluation.criteres.map((c) => (
          <div key={c.label} className="rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2" data-testid="demo-eval-criterion">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-xs font-semibold text-gray-800 truncate">{c.label}</span>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-500 flex-shrink-0">{c.weight} %</span>
              </div>
              <span className="text-xs font-bold flex-shrink-0" style={{ color: scoreColor(c.score) }}>{c.score}</span>
            </div>
            <div className="h-1.5 rounded-full bg-gray-200 mt-1 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${c.score}%`, backgroundColor: scoreColor(c.score) }} />
            </div>
            <p className="text-[11px] text-gray-500 mt-1">{c.comment}</p>
          </div>
        ))}
      </div>
    </div>

    <div className="grid sm:grid-cols-2 gap-2">
      <div className="rounded-lg bg-green-50 border border-green-200 p-3" data-testid="demo-eval-strengths">
        <h4 className="text-xs font-bold text-green-800 mb-1 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" /> Points forts</h4>
        {evaluation.points_forts.map(p => <p key={p} className="text-[11px] text-green-700 py-0.5">• {p}</p>)}
      </div>
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3" data-testid="demo-eval-improvements">
        <h4 className="text-xs font-bold text-amber-800 mb-1 flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Points à améliorer</h4>
        {evaluation.points_ameliorer.map(p => <p key={p} className="text-[11px] text-amber-700 py-0.5">• {p}</p>)}
      </div>
    </div>

    <div className="rounded-lg border-2 p-3 flex items-start gap-2" style={{ borderColor: '#C7D8FF', backgroundColor: '#F4F8FF' }} data-testid="demo-eval-encouragement">
      <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#2E6CF5' }} />
      <p className="text-xs font-medium" style={{ color: '#002768' }}>{evaluation.encouragement}</p>
    </div>
  </div>
);

const DemoCall = ({ protocol, onExit, standalone = false, presentation = false, onComplete }) => {
  const demo = DEMO_CALLS[protocol?.id];
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState(1);
  const [stepMode, setStepMode] = useState(false);
  const [typing, setTyping] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [nextIn, setNextIn] = useState(null);
  const [spotlight, setSpotlight] = useState(null);
  const [notesFlash, setNotesFlash] = useState(false);
  const [p1Flash, setP1Flash] = useState(false);
  const [muted, setMuted] = useState(false);
  const mutedRef = useRef(false);
  const audioCtxRef = useRef(null);
  const prevNotesRef = useRef(0);
  const prevPriorityRef = useRef(4);
  const chatRef = useRef(null);
  mutedRef.current = muted;

  const steps = demo ? demo.steps : [];
  const visible = steps.slice(0, idx);
  const done = idx >= steps.length;

  const phaseById = useMemo(() => Object.fromEntries((protocol?.phases || []).map(p => [p.id, p])), [protocol]);

  // Deterministic simulated clock (seconds at 1x) for note/phase timestamps
  const cumTimes = useMemo(() => {
    let t = 0;
    return steps.map(s => { const cur = Math.round(t); t += stepDelay(s) / 1000; return cur; });
  }, [steps]);
  const totalDuration = useMemo(() => steps.reduce((acc, s) => acc + stepDelay(s) / 1000, 0), [steps]);

  // Fold visible steps into simulation-like state (answers, flags, priority, notes)
  const derived = useMemo(() => {
    const answers = {}; const flags = []; const notes = []; const phaseTimes = {};
    let priority = 4; let curPhaseId = null;
    const addFlag = f => { if (!flags.includes(f)) flags.push(f); };
    visible.forEach((s, i) => {
      if (s.t === 'phase') {
        curPhaseId = s.id;
        if (s.id && phaseTimes[s.id] == null) phaseTimes[s.id] = cumTimes[i];
      } else if (s.t === 'note') {
        notes.push({ at: cumTimes[i], text: s.text });
      } else if (s.t === 'priority') {
        priority = Math.min(priority, s.level);
      } else if (s.t === 'ans') {
        const ph = phaseById[s.phase];
        if (!ph) return;
        if (phaseTimes[s.phase] == null) phaseTimes[s.phase] = cumTimes[i];
        if (s.aid) {
          const a = (ph.answers || []).find(x => x.id === s.aid);
          if (!a) return;
          answers[ph.id] = a.label;
          const isP1 = a.next === 'priority_1' || a.priority_change === 1;
          if (a.alert || isP1) addFlag((ph.question || ph.title) + ' → ' + a.label);
          if (isP1) priority = 1;
          else if (a.priority_change) priority = Math.min(priority, a.priority_change);
        } else if (Array.isArray(s.a)) {
          const cur = Array.isArray(answers[ph.id]) ? answers[ph.id] : [];
          answers[ph.id] = [...cur, ...s.a.filter(x => !cur.includes(x))];
        } else if (ph.type === 'info_gathering') {
          Object.assign(answers, s.a);
        } else {
          answers[ph.id] = { ...(answers[ph.id] || {}), ...s.a };
          Object.entries(s.a).forEach(([k, v]) => {
            const q = (ph.questions || []).find(qq => qq.id === k);
            if (q && ((q.red_flag && v === 'oui') || (q.type === 'multi_choice' && (q.red_flag_options || []).includes(v)))) {
              addFlag(q.question);
              priority = Math.min(priority, 2);
            }
          });
        }
      }
    });
    return { answers, flags, notes, phaseTimes, priority, curPhaseId };
  }, [visible, phaseById, cumTimes]);

  useEffect(() => {
    if (!playing || stepMode || done) return;
    if (spotlight) return; // pause de lecture pendant le projecteur pédagogique
    const next = steps[idx];
    const showTyping = next && (next.t === 'c' || next.t === 'd');
    if (showTyping) setTyping(true);
    const t1 = setTimeout(() => {
      setTyping(false);
      setIdx(i => i + 1);
    }, stepDelay(next || {}) / speed);
    return () => { clearTimeout(t1); setTyping(false); };
  }, [idx, playing, speed, stepMode, done, spotlight]);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [idx, typing]);

  // --- Effets "punch" ---
  const beep = (freq, dur, delay = 0, type = 'sine', vol = 0.07) => {
    if (mutedRef.current) return;
    if (!audioCtxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      audioCtxRef.current = new AC();
    }
    const ctx = audioCtxRef.current;
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    const t0 = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.linearRampToValueAtTime(vol, t0 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(g);
    g.connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + dur + 0.05);
  };
  const playDing = () => { beep(880, 0.15); beep(1318, 0.22, 0.09); };
  const playAlert = () => { beep(740, 0.16, 0, 'square', 0.05); beep(740, 0.16, 0.22, 'square', 0.05); beep(988, 0.4, 0.44, 'square', 0.06); };

  // Projecteur sur les encadrés pédagogiques
  useEffect(() => {
    const last = steps[idx - 1];
    if (last?.t === 'tip') {
      setSpotlight(last.text);
      const t = setTimeout(() => setSpotlight(null), Math.max(3000, 7000 / speed));
      return () => { clearTimeout(t); setSpotlight(null); };
    }
  }, [idx, steps, speed]);

  // Ding + flash du panneau quand une note est ajoutée
  useEffect(() => {
    const n = derived.notes.length;
    if (n > prevNotesRef.current) {
      setNotesFlash(true);
      playDing();
      const t = setTimeout(() => setNotesFlash(false), 1600);
      prevNotesRef.current = n;
      return () => clearTimeout(t);
    }
    prevNotesRef.current = n;
    // eslint-disable-next-line
  }, [derived.notes.length]);

  // Flash rouge en bordure d'écran + tonalité d'alerte au passage en Priorité 1
  useEffect(() => {
    if (derived.priority === 1 && prevPriorityRef.current !== 1) {
      setP1Flash(true);
      playAlert();
      const t = setTimeout(() => setP1Flash(false), 2600);
      prevPriorityRef.current = 1;
      return () => clearTimeout(t);
    }
    prevPriorityRef.current = derived.priority;
    // eslint-disable-next-line
  }, [derived.priority]);

  // Mode présentation : après la fin (temps de lire l'évaluation), enchaîner sur l'appel suivant
  useEffect(() => {
    if (!presentation || !done || !onComplete) return;
    setNextIn(30);
    const iv = setInterval(() => setNextIn(s => (s > 0 ? s - 1 : 0)), 1000);
    const t = setTimeout(onComplete, 30000);
    return () => { clearInterval(iv); clearTimeout(t); };
  }, [done, presentation]);

  if (!demo) return null;

  const pConfig = PRIORITY_CONFIG[derived.priority] || PRIORITY_CONFIG[4];
  const curPhase = derived.curPhaseId ? phaseById[derived.curPhaseId] : null;

  const printDemo = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    const infoPhase = (protocol.phases || []).find(p => p.type === 'info_gathering');
    const infoRows = infoPhase
      ? infoPhase.fields.filter(f => derived.answers[f.id]).map(f =>
          '<tr><td style="padding:3px 10px 3px 0;color:#64748b;font-size:13px;width:40%;">' + escHtml(f.label) + '</td>'
          + '<td style="padding:3px 0;font-size:13px;font-weight:600;color:#1e293b;">' + escHtml(derived.answers[f.id]) + '</td></tr>').join('')
      : '';
    const infoHtml = infoRows
      ? '<div style="margin-bottom:12px;padding:10px 12px;border:1px solid #e2e8f0;border-radius:8px;background:#f8fafc;">'
        + '<h4 style="font-size:14px;font-weight:bold;color:#002768;margin-bottom:6px;">Contrôle initial de l\'appel</h4>'
        + '<table style="width:100%;border-collapse:collapse;">' + infoRows + '</table></div>'
      : '';
    const rfHtml = derived.flags.length > 0
      ? '<div style="margin:14px 0;padding:12px;border-left:4px solid #DC2626;background:#FEF2F2;border-radius:8px;">'
        + '<h4 style="color:#DC2626;font-size:14px;font-weight:bold;margin-bottom:8px;">Drapeaux rouges (' + derived.flags.length + ')</h4>'
        + '<ul style="list-style:none;padding:0;">' + derived.flags.map(f => '<li style="font-size:13px;color:#B91C1C;padding:2px 0;">⚠ ' + escHtml(f) + '</li>').join('') + '</ul></div>'
      : '';
    const ev = demo.evaluation;
    const evalHtml = ev
      ? '<h3 style="font-size:14px;margin:16px 0 8px;color:#002768;">Exemple d\'évaluation automatisée — score global : '
        + '<span style="color:' + (ev.score_global >= 70 ? '#16A34A' : '#F59E0B') + ';">' + ev.score_global + ' / 100</span></h3>'
        + '<p style="font-size:11px;color:#94a3b8;margin-bottom:8px;">Grille établie par les instructeurs (pondération ajustable) — chaque appel étudiant reçoit automatiquement ce rapport.</p>'
        + '<table style="width:100%;border-collapse:collapse;margin-bottom:10px;">'
        + '<tr style="background:#f1f5f9;"><th style="text-align:left;padding:5px 8px;font-size:12px;">Critère</th><th style="padding:5px 8px;font-size:12px;">Pond.</th><th style="padding:5px 8px;font-size:12px;">Score</th><th style="text-align:left;padding:5px 8px;font-size:12px;">Commentaire</th></tr>'
        + ev.criteres.map(c =>
            '<tr style="border-bottom:1px solid #e2e8f0;"><td style="padding:5px 8px;font-size:12px;font-weight:600;">' + escHtml(c.label) + '</td>'
            + '<td style="padding:5px 8px;font-size:12px;text-align:center;color:#64748b;">' + c.weight + ' %</td>'
            + '<td style="padding:5px 8px;font-size:12px;text-align:center;font-weight:bold;color:' + (c.score >= 70 ? '#16A34A' : '#F59E0B') + ';">' + c.score + '</td>'
            + '<td style="padding:5px 8px;font-size:11px;color:#475569;">' + escHtml(c.comment) + '</td></tr>').join('')
        + '</table>'
        + '<div style="display:flex;gap:10px;margin-bottom:8px;">'
        + '<div style="flex:1;padding:8px 10px;background:#F0FDF4;border:1px solid #BBF7D0;border-radius:8px;"><strong style="font-size:12px;color:#166534;">Points forts</strong>'
        + ev.points_forts.map(p => '<p style="font-size:11px;color:#15803D;padding:1px 0;">• ' + escHtml(p) + '</p>').join('') + '</div>'
        + '<div style="flex:1;padding:8px 10px;background:#FFFBEB;border:1px solid #FDE68A;border-radius:8px;"><strong style="font-size:12px;color:#92400E;">Points à améliorer</strong>'
        + ev.points_ameliorer.map(p => '<p style="font-size:11px;color:#B45309;padding:1px 0;">• ' + escHtml(p) + '</p>').join('') + '</div></div>'
        + '<div style="padding:8px 12px;background:#F4F8FF;border:1px solid #C7D8FF;border-radius:8px;font-size:12px;color:#002768;font-style:italic;">✨ ' + escHtml(ev.encouragement) + '</div>'
      : '';
    const chatHtml = steps.map(s => {
      if (s.t === 'transfer') return '<div style="margin:4px 0;padding:8px 12px;border-radius:8px;background:#F5F3FF;border:1px solid #DDD6FE;"><strong style="font-size:12px;color:#6D28D9;">911 primaire (transfert):</strong> <span style="font-size:13px;font-style:italic;">' + escHtml(s.text) + '</span></div>';
      if (s.t === 'd') return '<div style="margin:4px 0;padding:8px 12px;border-radius:8px;background:#EFF6FF;"><strong style="font-size:12px;color:#1E40AF;">Répartitrice:</strong> <span style="font-size:13px;">' + escHtml(s.text) + '</span></div>';
      if (s.t === 'c') return '<div style="margin:4px 0;padding:8px 12px;border-radius:8px;background:#F0FDF4;"><strong style="font-size:12px;color:#166534;">Citoyen:</strong> <span style="font-size:13px;">' + escHtml(s.text) + '</span></div>';
      if (s.t === 'tip') return '<div style="margin:4px 0;padding:6px 12px;border-radius:8px;background:#FFFBEB;border:1px dashed #F59E0B;font-size:12px;color:#92400E;">💡 ' + escHtml(s.text) + '</div>';
      if (s.t === 'priority') return '<div style="margin:6px 0;padding:8px 12px;border-radius:8px;background:#FEE2E2;border:2px solid #DC2626;font-size:13px;font-weight:bold;color:#B91C1C;">🚩 PRIORITÉ ' + s.level + ' ATTRIBUÉE — ' + escHtml(s.reason || '') + '</div>';
      return '';
    }).join('');
    w.document.write('<!DOCTYPE html><html><head><title>Démonstration — ' + escHtml(protocol.title) + '</title>'
      + '<style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:Segoe UI,Arial,sans-serif;padding:30px;color:#1e293b;line-height:1.5;}'
      + '.header{text-align:center;margin-bottom:20px;padding-bottom:12px;border-bottom:2px solid #e2e8f0;}'
      + '.meta{display:flex;gap:20px;flex-wrap:wrap;padding:10px 14px;background:#f8fafc;border-radius:8px;margin-bottom:14px;font-size:13px;}</style></head><body>'
      + '<div class="header"><h1 style="font-size:18px;color:#002768;">Démonstration — ' + escHtml(protocol.title) + '</h1>'
      + '<p style="font-size:12px;color:#64748b;">' + escHtml(demo.title) + ' • Appelant : ' + escHtml(demo.caller) + '</p></div>'
      + '<div class="meta"><div><strong>Mode:</strong> Démonstration (appel modèle)</div>'
      + '<div><strong>Date:</strong> ' + new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) + '</div>'
      + '<div><strong>Durée simulée:</strong> ' + formatChrono(Math.round(totalDuration)) + '</div>'
      + '<div><strong>Poste ressource 911:</strong> ' + escHtml(demo.poste) + ' — répartiteur ' + escHtml(demo.dispatcher) + '</div></div>'
      + '<div style="padding:12px;border:2px solid ' + pConfig.color + ';background:' + pConfig.bg + ';border-radius:10px;margin-bottom:14px;">'
      + '<h2 style="font-size:16px;color:' + pConfig.color + ';">' + pConfig.label + ' — ' + pConfig.title + '</h2></div>'
      + infoHtml + rfHtml
      + buildPhasesHtml(protocol, derived.answers, derived.phaseTimes)
      + buildNotesHtml(derived.notes)
      + evalHtml
      + '<h3 style="font-size:14px;margin:16px 0 8px;color:#002768;">Conversation</h3>' + chatHtml
      + '<div style="margin-top:20px;text-align:center;font-size:11px;color:#94a3b8;">Simulateur 911 — Mode Démonstration — ' + new Date().toLocaleDateString('fr-FR') + '</div>'
      + '</body></html>');
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  // ---------- Chat bubbles ----------
  const bubble = (s, i) => {
    if (s.t === 'transfer') return (
      <div key={i} className="flex justify-start">
        <div className="max-w-[88%] rounded-2xl rounded-bl-sm px-4 py-2.5 border-2" style={{ borderColor: '#7C3AED', backgroundColor: '#F5F3FF' }} data-testid="demo-transfer-message">
          <div className="flex items-center gap-1.5 mb-0.5">
            <Radio className="w-3 h-3 text-purple-700" />
            <span className="text-[10px] font-semibold text-purple-700">911 primaire — transfert d'appel</span>
          </div>
          <p className="text-sm text-gray-800 italic">« {s.text} »</p>
        </div>
      </div>
    );
    if (s.t === 'c') return (
      <div key={i} className="flex justify-start">
        <div className="max-w-[80%] rounded-2xl rounded-bl-sm px-3.5 py-2.5" style={{ backgroundColor: '#F1F5F9' }}>
          <div className="flex items-center gap-1.5 mb-0.5">
            <Bot className="w-3 h-3 text-gray-500" />
            <p className="text-[10px] font-semibold text-gray-500">Citoyen</p>
          </div>
          <p className="text-sm text-gray-800">{s.text}</p>
        </div>
      </div>
    );
    if (s.t === 'd') return (
      <div key={i} className="flex justify-end">
        <div className="max-w-[80%] rounded-2xl rounded-br-sm px-3.5 py-2.5 text-white" style={{ backgroundColor: '#002768' }}>
          <div className="flex items-center gap-1.5 mb-0.5">
            <User className="w-3 h-3 opacity-75" />
            <p className="text-[10px] font-semibold opacity-75">Répartitrice</p>
          </div>
          <p className="text-sm">{s.text}</p>
        </div>
      </div>
    );
    if (s.t === 'tip') return (
      <div key={i} className="flex justify-center animate-in fade-in">
        <div className="max-w-[90%] rounded-xl px-3.5 py-2.5 bg-amber-50 border-2 border-amber-300 flex items-start gap-2">
          <Lightbulb className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-900 font-medium">{s.text}</p>
        </div>
      </div>
    );
    if (s.t === 'phase') {
      const ph = phaseById[s.id];
      return (
        <div key={i} className="flex items-center gap-2 my-1">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400">{ph ? `Phase ${ph.number} — ${ph.title}` : s.label}</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>
      );
    }
    if (s.t === 'note') return (
      <div key={i} className="flex justify-center">
        <div className="demo-note-pop text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-3 py-1 flex items-center gap-1.5">
          <FileText className="w-3 h-3" /> Note d'appel : {s.text}
        </div>
      </div>
    );
    if (s.t === 'priority') return (
      <div key={i} className="flex justify-center">
        <div className="demo-priority-pop rounded-xl px-4 py-2.5 bg-red-50 border-2 border-red-400 text-center">
          <p className="text-sm font-bold text-red-700 flex items-center justify-center gap-1.5"><Flag className="w-4 h-4" /> PRIORITÉ {s.level} ATTRIBUÉE</p>
          <p className="text-xs text-red-600 mt-0.5">{s.reason}</p>
        </div>
      </div>
    );
    if (s.t === 'end') return (
      <div key={i} className="flex justify-center">
        <div className="rounded-xl px-4 py-3 bg-green-50 border-2 border-green-300 text-center max-w-[90%]">
          <p className="text-sm font-bold text-green-800 flex items-center justify-center gap-1.5"><CheckCircle className="w-4 h-4" /> Fin de la démonstration</p>
          <p className="text-xs text-green-700 mt-1">{s.text}</p>
        </div>
      </div>
    );
    return null;
  };

  return (
    <div className={`h-screen flex flex-col ${standalone ? '' : 'pt-20'}`} data-testid="demo-call-page">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-white flex-wrap gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={onExit} className="p-2 rounded-lg hover:bg-gray-100 flex-shrink-0" data-testid="demo-exit-btn">
            <ArrowLeft className="w-5 h-5 text-gray-500" />
          </button>
          <div className="min-w-0">
            <h1 className="text-sm font-bold truncate" style={{ color: '#002768' }}>{demo.title}</h1>
            <p className="text-[11px] text-gray-400 truncate">Appelant : {demo.caller} • Démonstration automatique</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <div className="px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: pConfig.bg, color: pConfig.color }} data-testid="demo-priority-badge">
            {pConfig.label}
          </div>
          {derived.flags.length > 0 && (
            <div className="px-2 py-1 rounded-full text-[11px] font-bold bg-red-100 text-red-700" data-testid="demo-flags-count">
              {derived.flags.length} alerte{derived.flags.length > 1 ? 's' : ''}
            </div>
          )}
          {!stepMode && (
            <button
              onClick={() => setPlaying(p => !p)}
              className="px-3 py-1.5 rounded-lg text-white text-xs font-bold flex items-center gap-1.5"
              style={{ backgroundColor: playing ? '#F59E0B' : '#16A34A' }}
              data-testid="demo-play-pause-btn"
            >
              {playing ? <><Pause className="w-3.5 h-3.5" /> Pause</> : <><Play className="w-3.5 h-3.5" /> Lecture</>}
            </button>
          )}
          {stepMode && !done && (
            <button
              onClick={() => setIdx(i => i + 1)}
              className="px-3 py-1.5 rounded-lg text-white text-xs font-bold flex items-center gap-1.5"
              style={{ backgroundColor: '#2E6CF5' }}
              data-testid="demo-next-step-btn"
            >
              <StepForward className="w-3.5 h-3.5" /> Étape suivante
            </button>
          )}
          <button
            onClick={() => setStepMode(m => { const nm = !m; setPlaying(!nm); return nm; })}
            className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold border-2 ${stepMode ? 'border-blue-500 text-blue-600 bg-blue-50' : 'border-gray-200 text-gray-500'}`}
            data-testid="demo-step-mode-btn"
            title="Mode pas à pas — idéal en classe"
          >
            Pas à pas
          </button>
          {[1, 1.5, 2].map(v => (
            <button
              key={v}
              onClick={() => setSpeed(v)}
              className={`px-2 py-1.5 rounded-lg text-xs font-semibold border-2 ${speed === v ? 'border-blue-500 text-blue-600 bg-blue-50' : 'border-gray-200 text-gray-400'}`}
              data-testid={`demo-speed-${v}-btn`}
            >
              {v}x
            </button>
          ))}
          <button
            onClick={() => setMuted(m => !m)}
            className={`p-1.5 rounded-lg border-2 ${muted ? 'border-gray-200 text-gray-400' : 'border-blue-200 text-blue-600 bg-blue-50'}`}
            data-testid="demo-mute-btn"
            title={muted ? 'Activer le son' : 'Couper le son'}
          >
            {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
          </button>
          {done && (
            <button
              onClick={printDemo}
              className="px-3 py-1.5 rounded-lg text-white text-xs font-bold flex items-center gap-1.5"
              style={{ backgroundColor: '#002768' }}
              data-testid="demo-print-btn"
            >
              <Printer className="w-3.5 h-3.5" /> Imprimer
            </button>
          )}
          <button
            onClick={() => { setIdx(0); setPlaying(true); setQuizAnswers({}); }}
            className="p-1.5 rounded-lg border-2 border-gray-200 text-gray-500 hover:bg-gray-50"
            data-testid="demo-restart-btn"
            title="Recommencer"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-gray-100 overflow-hidden flex-shrink-0">
        <div className="h-full transition-all duration-500" style={{ width: `${(idx / steps.length) * 100}%`, backgroundColor: '#2E6CF5' }} />
      </div>

      {/* Main: protocol left + chat right (like simulation) */}
      <div className="flex-1 flex flex-col-reverse lg:flex-row overflow-hidden">
        {/* LEFT: auto-filled protocol */}
        <div className="lg:w-[380px] lg:border-r border-t lg:border-t-0 flex flex-col bg-gray-50 overflow-hidden max-h-[45vh] lg:max-h-none flex-shrink-0">
          {/* Phase pills */}
          <div className="px-3 py-2 border-b bg-white flex-shrink-0">
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1" data-testid="demo-phase-progress">
              {protocol.phases.map(phase => {
                const visited = derived.phaseTimes[phase.id] != null;
                const isActive = phase.id === derived.curPhaseId && !done;
                return (
                  <div
                    key={phase.id}
                    className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold"
                    style={{
                      backgroundColor: isActive ? '#2E6CF5' : visited ? '#22C55E' : '#E5E7EB',
                      color: isActive || visited ? 'white' : '#9CA3AF',
                    }}
                    title={`Phase ${phase.number} — ${phase.title}`}
                  >
                    {visited && !isActive ? '✓' : phase.number}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Current phase content / final summary */}
          <div className="flex-1 overflow-y-auto p-3">
            {done ? (
              <div className="space-y-3" data-testid="demo-summary-panel">
                <div className="p-3 rounded-lg border-2" style={{ borderColor: pConfig.color, backgroundColor: pConfig.bg }}>
                  <h3 className="text-sm font-bold" style={{ color: pConfig.color }}>{pConfig.label}</h3>
                  <p className="text-xs" style={{ color: pConfig.color }}>{pConfig.title}</p>
                </div>
                {derived.flags.length > 0 && (
                  <div className="p-2 rounded-lg bg-red-50 border border-red-200">
                    <h4 className="text-xs font-bold text-red-700 mb-1">Drapeaux rouges ({derived.flags.length})</h4>
                    {derived.flags.map((f, i) => <p key={`${i}-${f}`} className="text-[11px] text-red-600">⚠ {f}</p>)}
                  </div>
                )}
                <CallNotesCard notes={derived.notes} />
                <div className="flex gap-2">
                  <button onClick={printDemo} className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-semibold text-white" style={{ backgroundColor: '#002768' }} data-testid="demo-summary-print-btn">
                    <Printer className="w-3.5 h-3.5" /> Imprimer
                  </button>
                  <button onClick={onExit} className="flex-1 flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-semibold border border-gray-300 text-gray-600 hover:bg-gray-50" data-testid="demo-summary-exit-btn">
                    <RotateCcw className="w-3.5 h-3.5" /> Terminer
                  </button>
                </div>
              </div>
            ) : curPhase ? (
              <div>
                <div className="flex items-center gap-1.5 py-2">
                  <Activity className="w-3.5 h-3.5" style={{ color: '#002768' }} />
                  <span className="text-xs font-bold" style={{ color: '#002768' }}>
                    Phase {curPhase.number} — {curPhase.title}
                  </span>
                </div>
                <DemoPhaseView phase={curPhase} answers={derived.answers} />
              </div>
            ) : (
              <p className="text-xs text-gray-400 text-center py-6">Transfert de l'appel en cours…</p>
            )}

            {/* Red flags during the call */}
            {!done && derived.flags.length > 0 && (
              <div className="mt-3 p-2 rounded-lg bg-red-50 border border-red-200" data-testid="demo-flags-panel">
                <h4 className="text-xs font-bold text-red-700 mb-1">Drapeaux rouges ({derived.flags.length})</h4>
                {derived.flags.map((f, i) => <p key={`${i}-${f}`} className="text-[11px] text-red-600">⚠ {f}</p>)}
              </div>
            )}

            {/* Timestamped call notes */}
            {!done && (
              <div className={`mt-3 p-3 rounded-xl border border-amber-200 bg-amber-50 ${notesFlash ? 'demo-notes-flash' : ''}`} data-testid="demo-notes-panel">
                <div className="flex items-center gap-1.5 mb-2">
                  <FileText className="w-3.5 h-3.5 text-amber-600" />
                  <span className="text-xs font-bold text-amber-800">Notes de l'appel</span>
                  {derived.notes.length > 0 && <span className="text-[10px] text-amber-600 ml-auto">{derived.notes.length}</span>}
                </div>
                {derived.notes.length > 0 ? (
                  <ul className="space-y-1" data-testid="demo-notes-list">
                    {derived.notes.map((n, i) => (
                      <li key={i} className="text-xs text-amber-900 flex gap-1.5">
                        <span className="font-mono text-[10px] text-amber-600 flex-shrink-0 pt-0.5">{formatChrono(n.at)}</span>
                        <span>{n.text}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-amber-400">Aucune note pour l'instant</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: chat */}
        <div className="flex-1 flex flex-col bg-white overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 space-y-3" ref={chatRef} data-testid="demo-chat-area">
            <div className="text-center py-1">
              <div className="inline-block px-4 py-2 rounded-lg bg-gray-100 text-xs text-gray-500">
                Appel entrant — {demo.title}
              </div>
            </div>
            {visible.map(bubble)}
            {typing && (
              <div className={`flex ${steps[idx]?.t === 'd' ? 'justify-end' : 'justify-start'}`}>
                <div className="rounded-2xl px-4 py-2.5 bg-gray-200 text-gray-500 text-sm">
                  <span className="inline-flex gap-1"><span className="animate-bounce">•</span><span className="animate-bounce" style={{ animationDelay: '120ms' }}>•</span><span className="animate-bounce" style={{ animationDelay: '240ms' }}>•</span></span>
                </div>
              </div>
            )}

            {/* Exemple d'évaluation automatisée (avant le quiz) */}
            {done && demo.evaluation && <DemoEvaluationCard evaluation={demo.evaluation} />}

            {/* Quiz éclair post-démonstration (masqué en mode présentation) */}
            {done && demo.quiz && !presentation && (
              <div className="rounded-2xl border-2 border-blue-200 bg-white p-4 space-y-4" data-testid="demo-quiz">
                <div>
                  <h3 className="text-sm font-bold" style={{ color: '#002768' }}>⚡ Quiz éclair — avez-vous bien observé?</h3>
                  <p className="text-xs text-gray-400">3 questions sur l'appel que vous venez de regarder.</p>
                </div>
                {demo.quiz.map((qz, qi) => {
                  const picked = quizAnswers[qi];
                  return (
                    <div key={qi}>
                      <p className="text-sm font-semibold text-gray-800 mb-1.5">{qi + 1}. {qz.q}</p>
                      <div className="space-y-1.5">
                        {qz.options.map((opt, oi) => {
                          const isPicked = picked === oi;
                          const isCorrect = oi === qz.correct;
                          let cls = 'border-gray-200 hover:border-blue-300 bg-white';
                          if (picked != null) {
                            if (isCorrect) cls = 'border-green-500 bg-green-50';
                            else if (isPicked) cls = 'border-red-400 bg-red-50';
                            else cls = 'border-gray-100 bg-gray-50 opacity-60';
                          }
                          return (
                            <button
                              key={oi}
                              disabled={picked != null}
                              onClick={() => setQuizAnswers(prev => ({ ...prev, [qi]: oi }))}
                              className={`w-full text-left px-3 py-2 rounded-lg border-2 text-xs transition-colors ${cls}`}
                              data-testid={`demo-quiz-q${qi}-opt${oi}`}
                            >
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                      {picked != null && (
                        <p className={`text-xs mt-1.5 px-3 py-2 rounded-lg ${picked === qz.correct ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'}`} data-testid={`demo-quiz-q${qi}-feedback`}>
                          {picked === qz.correct ? '✔ Exact! ' : '✘ Pas tout à fait. '}{qz.explain}
                        </p>
                      )}
                    </div>
                  );
                })}
                {Object.keys(quizAnswers).length === demo.quiz.length && (
                  <div className="rounded-xl px-4 py-3 text-center border-2" style={{ borderColor: '#2E6CF5', backgroundColor: '#EFF6FF' }} data-testid="demo-quiz-score">
                    <p className="text-sm font-bold" style={{ color: '#002768' }}>
                      Résultat : {demo.quiz.filter((qz, qi) => quizAnswers[qi] === qz.correct).length} / {demo.quiz.length}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {demo.quiz.every((qz, qi) => quizAnswers[qi] === qz.correct)
                        ? 'Parfait! Vous êtes prêt·e à essayer le Mode Guidé.'
                        : 'Relisez les explications, puis revoyez la démonstration au besoin (bouton ⟲).'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Compte à rebours du mode présentation */}
      {presentation && done && nextIn != null && (
        <div className="fixed bottom-4 right-4 z-50 px-4 py-2 rounded-full text-white text-xs font-bold shadow-lg flex items-center gap-2" style={{ backgroundColor: '#002768' }} data-testid="presentation-countdown">
          <Play className="w-3.5 h-3.5" /> Appel suivant dans {nextIn} s
        </div>
      )}

      {/* Flash rouge en bordure d'écran (Priorité 1) */}
      {p1Flash && (
        <div className="fixed inset-0 pointer-events-none z-[70] demo-edge-flash" style={{ boxShadow: 'inset 0 0 130px 35px rgba(220,38,38,0.8)' }} data-testid="demo-p1-flash" />
      )}

      {/* Projecteur : encadré pédagogique agrandi au centre */}
      {spotlight && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-6" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)' }} data-testid="demo-spotlight">
          <div className="demo-spotlight-card max-w-2xl rounded-2xl bg-amber-50 border-4 border-amber-400 px-8 py-6 shadow-2xl flex items-start gap-4">
            <Lightbulb className="w-10 h-10 text-amber-500 flex-shrink-0 mt-1" />
            <p className="text-lg sm:text-2xl font-bold text-amber-900 leading-snug">{spotlight}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoCall;
